// NOTICE: KAPTION-INTEGRATION
// Helpers for single-line inputs that hold a WhatsApp template variable.
//
// The {{1}} variable of a WhatsApp utility template cannot contain newlines,
// tabs, or long runs of whitespace - Meta rejects the send with a parameter
// format error. The utility-template editor is therefore a single-line
// contenteditable, and anything arriving from a paste, a drag-and-drop or a
// programmatic set has to be flattened onto one line before it becomes the
// model value.

// Line separators we treat as "this is a new line": CR, LF, CRLF, and the
// Unicode LINE/PARAGRAPH SEPARATOR characters that word processors emit.
const LINE_BREAKS = /\r\n|[\r\n\u2028\u2029]/g;

// Non-breaking space. contenteditable inserts these for consecutive spaces, and
// they are indistinguishable from a normal space to the user.
const NON_BREAKING_SPACE = /\u00a0/g;

// Elements that imply a line break when we read text out of a contenteditable.
const BLOCK_LEVEL_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HR',
  'LI',
  'MAIN',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SECTION',
  'TABLE',
  'TD',
  'TH',
  'TR',
  'UL',
]);

/**
 * Remove line breaks without touching any other whitespace.
 *
 * This is what runs on every keystroke: it guarantees the value stays on one
 * line, but preserves the spaces the user is actively typing. Collapsing or
 * trimming here would eat a trailing space as fast as it is typed.
 *
 * @param {*} value
 * @returns {string}
 */
export const stripLineBreaks = value => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(LINE_BREAKS, ' ')
    .replace(NON_BREAKING_SPACE, ' ');
};

/**
 * Flatten arbitrary text onto a single clean line.
 *
 * Newlines become a single space, runs of whitespace collapse to one space, and
 * the result is trimmed. Nothing is dropped - a multi-line paste keeps all of
 * its words, they just end up on one line.
 *
 * @param {*} value
 * @returns {string}
 */
export const flattenToSingleLine = value => {
  return stripLineBreaks(value).replace(/\s+/g, ' ').trim();
};

/**
 * True when flattening `value` would actually change it, i.e. the text really is
 * multi-line (or carries whitespace that has to be collapsed). Used to decide
 * whether the user deserves an explanation.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const needsFlattening = value => {
  if (value === null || value === undefined) return false;
  const text = String(value);
  return text !== flattenToSingleLine(text);
};

/**
 * Read the plain text out of a DOM node the way a human reads it.
 *
 * `Node.textContent` concatenates text nodes and inserts nothing at element
 * boundaries, so a pasted `<div>Line one</div><div>Line two</div>` reads back as
 * "Line oneLine two" - the words are silently glued together. This walks the
 * tree instead and emits a newline at `<br>` and at block-element boundaries, so
 * the caller can flatten those into real spaces.
 *
 * @param {Node} node
 * @returns {string}
 */
export const extractPlainText = node => {
  if (!node) return '';
  // Text node
  if (node.nodeType === 3) return node.nodeValue || '';
  // Anything that is not an element (comment, etc.) contributes nothing
  if (node.nodeType !== 1) return '';

  const tagName = (node.tagName || '').toUpperCase();
  if (tagName === 'BR') return '\n';

  let text = '';
  const children = node.childNodes || [];
  for (let index = 0; index < children.length; index += 1) {
    text += extractPlainText(children[index]);
  }

  return BLOCK_LEVEL_TAGS.has(tagName) ? `\n${text}\n` : text;
};

/**
 * Read a contenteditable element and return its value as one clean line.
 *
 * @param {HTMLElement} element
 * @returns {string}
 */
export const readEditableAsSingleLine = element => {
  if (!element) return '';
  return flattenToSingleLine(extractPlainText(element));
};

/**
 * True when the element holds markup that breaks the single-line contract
 * (block children or `<br>`), which is what a native contenteditable paste
 * leaves behind. When this is true the DOM has to be rewritten, not just read.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
export const hasMultiLineMarkup = element => {
  if (!element) return false;
  const children = element.childNodes || [];
  for (let index = 0; index < children.length; index += 1) {
    if (children[index].nodeType === 1) return true;
  }
  return false;
};
